import classnames from "classnames";
import find from "lodash-es/find";
import React, { PureComponent, ReactNode } from "react";
import { TierPriceButtons } from "src/components/ProductAddButton";
import withRenderOn from "src/hoc/withRenderOn";
import { IRenderOn } from "src/hooks/useRenderOn";
import styled from "styled-components";

import { ProductAddButton as AddButton } from "..";
import { IUserWishAddProps } from "../../hoc/persister/userWishAdd/userWishAdd";
import { IUserWishRemoveProps } from "../../hoc/persister/userWishRemove/userWishRemove";
import { IWithWishList } from "../../hoc/repository/withWishList/withWishList";
import { IWithAuthenticated } from "../../hoc/withAuthenticated/withAuthenticated";
import { translate } from "../../services/translation.service";
import globalTheme, { media } from "../../theme";
import { IProduct } from "../../types/product";
import Hyperlink from "../Hyperlink";
import { Col, Row } from "../Layout";
import Price from "../Price";
import { P } from "../Semantic";

import {
  ImageWrapper,
  ProductDetails,
  ProductRating,
  ProductReassuranceBar,
  ProductShortDescription,
  ProductSubtitle,
  ProductTitle,
} from "./components";
import { ILayout, IPageType, ITheme } from "./types";

type ITitleWrapperProps = Required<Pick<IProps, "theme">>;
type IWishlistWrapperProps = Required<Pick<IProps, "theme">>;
type IImageWrapperProps = Required<Pick<IProps, "theme">>;
type IImageProps = Pick<IProps, "imageMaxHeight">;

const TitleWrapper = styled.div<ITitleWrapperProps>``;
const ProductInfo = styled.div``;
const StyledPrice = styled(P)``;

const InStock = styled.div`
  font-size: ${globalTheme.fontSizes.big};
  color: ${globalTheme.colors.black};
  text-transform: uppercase;
  text-align: center;

  ${media.tablet} {
    text-align: left;
  }
`;

const OriginalPrice = styled.span``;

const WhiskyliveLabel = styled.div``;
const ProductLink = styled(Hyperlink)``;

const Ratings = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${globalTheme.margins.marginMedium};

  & > * {
    margin: 0;
  }

  ${media.tablet} {
    justify-content: flex-start;
  }
`;

const ReviewsLink = styled.div`
  display: inline-block;
  margin: 3px 0 0 15px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const PriceWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;

  section {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin: 40px 0 5px 0;
  }
`;

const StyledCol = styled(Col)`
  ${media.laptop} {
    padding-left: 30px;
    padding-right: 30px;
  }
  ${media.tablet} {
    &.right-separator {
      border-right: 1px solid ${globalTheme.colors.veryLightGrey};
    }
  }
`;
const ProductAddButton = styled((props) => <AddButton {...props} />)``;

interface IWrapper
  extends ITitleWrapperProps,
    IImageProps,
    IImageWrapperProps,
    IWishlistWrapperProps,
    ILayout {}

const Wrapper = styled.section<IWrapper>`
  &.product-item {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    margin-top: ${globalTheme.margins.paddingPageBig}px;

    ${ProductInfo} {
      max-width: 60%;
    }

    ${InStock} {
      display: block;
      margin: 0;
    }

    ${ProductLink} {
      div {
        &:hover {
          text-decoration: none;
        }
      }
    }

    ${TitleWrapper} {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      align-items: center;
      justify-content: center;
      text-align: center;

      ${media.tablet} {
        align-items: flex-start;
      }

      margin-top: ${(props: ITitleWrapperProps): string =>
        props.theme === "mini" || props.theme === "newArrivals"
          ? globalTheme.margins.marginMedium
          : "0"};
    }

    ${StyledPrice} {
      margin-top: 5px;
      margin-bottom: 0px;
      font-family: ${globalTheme.fonts.regular};
      font-size: ${globalTheme.fontSizes.regular};

      display: flex;
      align-items: baseline;
      justify-content: center;

      &.out-of-stock {
        color: ${globalTheme.colors.veryLightGrey};
      }

      ${media.tablet} {
        justify-content: flex-start;
      }
    }

    ${OriginalPrice} {
      margin-left: 12px;
      font-family: ${globalTheme.fonts.light};
      text-decoration: line-through;
    }

    ${WhiskyliveLabel} {
      position: absolute;
      width: 80px;
      height: 80px;
      right: -8px;
      top: 65px;
      align-self: flex-start;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #000;
      border-radius: 50%;

      span {
        color: #fff;
        text-align: center;
        letter-spacing: 1.8px;
        line-height: 1.2;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
      }
    }

    /**
     * Horizontal layout
     * (Focus product on new arrivals)
     */
    &.layout-horizontal {
      flex-direction: row;

      ${ProductLink} {
        div {
          color: ${globalTheme.colors.grey};
          font-size: ${globalTheme.fontSizes.tiny};
          font-weight: 400;
          text-decoration: underline;
        }
      }

      ${TitleWrapper} {
        align-items: flex-start;
        text-transform: uppercase;
      }

      ${StyledPrice} {
        display: inline-block;
        font-size: ${globalTheme.fontSizes.huge};
        font-family: ${globalTheme.fonts.subtitle};
      }

      .product-add-button {
        margin-top: 10px;
      }
    }

    /**
     * Desktop layout (2 columns)
     */
    &.layout-product-page {
      ${StyledPrice} {
        margin: 20px 0 10px 0;
        font-size: ${globalTheme.fontSizes.huge};
      }

      ${InStock} {
        font-family: ${globalTheme.fonts.granville};
      }

      ${ProductAddButton} {
        button {
          padding: 0 20px;
        }
      }
    }
  }
`;

const PresaleBlock = styled.div`
  color: green;
  margin-top: 5px;
  text-align: center;

  ${media.tablet} {
    text-align: left;
  }
`;

const PriceStock = styled.section`
  && {
    align-items: baseline;

    ${InStock} {
      margin-left: ${globalTheme.margins.marginBase};
    }
  }
`;

const GroupPrices = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  ${media.tablet} {
    flex-direction: row;
  }
`;

const Presale = ({ product }: { product: IProduct }): null | JSX.Element => {
  const presaleDate = new Date(
    (product.presaleShippingDate || "").replace(" ", "T")
  );

  const zeroFill = (num: number): string =>
    num >= 0 && num < 10 ? "0" + num : num + "";

  if (!product.presale || isNaN(+presaleDate)) {
    return null;
  }

  return (
    <PresaleBlock>
      {translate("product.availableFrom") +
        " " +
        zeroFill(presaleDate.getDate()) +
        "/" +
        zeroFill(presaleDate.getMonth() + 1) +
        "/" +
        presaleDate.getFullYear()}
    </PresaleBlock>
  );
};

export interface IProps
  extends IWithWishList,
    IUserWishAddProps,
    IUserWishRemoveProps,
    IWithAuthenticated,
    ITheme,
    ILayout,
    IPageType {
  canBeAddedToCart?: boolean;
  hideSubTitle?: boolean;
  className?: string;
  displayButton?: boolean;
  hasNoProductLink?: boolean;
  imageMaxHeight?: number;
  onClick?: () => void;
  product: IProduct;
  showShortDescription?: boolean;
  showIsInStock?: boolean;
  goToReviews?: () => void;
  goToAccordion?: () => void;
  renderOnProps?: IRenderOn;
}

class ProductWrapper extends PureComponent<IProps> {
  public static defaultProps: Partial<IProps> = {
    canBeAddedToCart: false,
    displayButton: true,
    hideSubTitle: false,
    theme: "category",
  };

  public handleClick = (): void => {
    const { onClick } = this.props;

    if (onClick) {
      onClick();
    }
  };

  public isProductWished = (
    product: IProduct | null,
    userWishlist: string[]
  ): boolean => {
    if (!product || !userWishlist) {
      return false;
    }

    return !!find(
      userWishlist,
      (wishedProduct: string) => wishedProduct === product.id
    );
  };

  public render(): ReactNode {
    const {
      canBeAddedToCart,
      hideSubTitle,
      className,
      displayButton,
      imageMaxHeight,
      product,
      theme,
      layout,
      hasNoProductLink,
      wishList,
      showShortDescription,
      showIsInStock,
      goToReviews,
      goToAccordion,
      renderOnProps: { smOrUp = false, xs = false } = {},
    } = this.props;

    if (!product) {
      return null;
    }

    const { url, name, displayRating = true } = product;

    const outOfStock = !!(product && product.outOfStock);
    const isOutOfStockClassname = {
      "out-of-stock": outOfStock,
    };

    const isWished = this.isProductWished(product, wishList);

    /**
     * Horizontal layout
     * (Focus product on new arrivals)
     */
    if (layout === "horizontal") {
      return (
        <Wrapper
          className={classnames(className, "product-item", "layout-horizontal")}
          theme={theme}
          layout={layout}
        >
          <ImageWrapper
            product={product}
            isOutOfStockClassname={isOutOfStockClassname}
            outOfStock={outOfStock}
            isWished={isWished}
            isAuthenticated={this.props.isAuthenticated}
            userWishAdd={this.props.userWishAdd}
            userWishRemove={this.props.userWishRemove}
            handleClick={this.handleClick}
            imageMaxHeight={imageMaxHeight}
            hasNoProductLink={hasNoProductLink}
            theme={theme}
            url={url}
          />

          <ProductInfo>
            <TitleWrapper theme={theme} onClick={this.handleClick}>
              <ProductTitle
                hasNoProductLink={hasNoProductLink}
                url={url}
                name={name}
                pageType={this.props.pageType}
                theme={theme}
              />
              <ProductSubtitle product={product} hideSubTitle={hideSubTitle} />
            </TitleWrapper>

            <ProductRating stars={this.props.product.stars} />
            <ProductDetails product={product} theme={theme} />

            <ProductShortDescription
              showShortDescription={showShortDescription}
              shortDescription={product.shortDescription}
            />

            <ProductLink to={url}>
              <div>{translate("global.seeMore")}</div>
            </ProductLink>

            <PriceWrapper>
              <PriceStock>
                <StyledPrice
                  className={classnames(isOutOfStockClassname, "product-price")}
                >
                  <Price
                    actualPrice={product.actualPrice}
                    originalPrice={product.originalPrice}
                  />
                </StyledPrice>
                {showIsInStock && !outOfStock && product.availabilityLabel && (
                  <InStock>{product.availabilityLabel}</InStock>
                )}
              </PriceStock>

              <Presale product={product}></Presale>

              {displayButton && (
                <ProductAddButton
                  canBeAddedToCart={canBeAddedToCart}
                  product={product}
                  fullWidth={false}
                />
              )}
            </PriceWrapper>
          </ProductInfo>
        </Wrapper>
      );
    }

    /**
     * ProductPage layout
     */
    if (layout === "productPage") {
      return (
        <Wrapper
          className={classnames(
            className,
            "product-item",
            "layout-product-page"
          )}
          theme={theme}
          layout={layout}
        >
          <Row>
            <StyledCol md={6} className="right-separator">
              {xs && (
                <TitleWrapper theme={theme} onClick={this.handleClick}>
                  <ProductTitle
                    hasNoProductLink={hasNoProductLink}
                    url={url}
                    name={name}
                    pageType={this.props.pageType}
                    theme={theme}
                  />
                  <ProductSubtitle
                    product={product}
                    hideSubTitle={hideSubTitle}
                  />
                </TitleWrapper>
              )}
              <ImageWrapper
                product={product}
                isOutOfStockClassname={isOutOfStockClassname}
                outOfStock={outOfStock}
                isWished={isWished}
                isAuthenticated={this.props.isAuthenticated}
                userWishAdd={this.props.userWishAdd}
                userWishRemove={this.props.userWishRemove}
                handleClick={this.handleClick}
                imageMaxHeight={imageMaxHeight}
                hasNoProductLink={true}
                theme={theme}
                url={url}
              />
            </StyledCol>
            <StyledCol md={6}>
              {smOrUp && (
                <TitleWrapper theme={theme} onClick={this.handleClick}>
                  <ProductTitle
                    hasNoProductLink={hasNoProductLink}
                    url={url}
                    name={name}
                    pageType={this.props.pageType}
                    theme={theme}
                  />
                  <ProductDetails
                    product={product}
                    theme={theme}
                    layout={layout}
                  />
                </TitleWrapper>
              )}

              <Ratings>
                <ProductRating stars={this.props.product.stars} />
                {this.props.product.productReviewCount && smOrUp && (
                  <ReviewsLink onClick={goToReviews}>
                    {this.props.product.productReviewCount}{" "}
                    {translate("product.reviews")}
                  </ReviewsLink>
                )}
              </Ratings>

              <GroupPrices>
                <StyledPrice
                  className={classnames(isOutOfStockClassname, "product-price")}
                >
                  {product.displayPrice && (
                    <Price
                      actualPrice={product.actualPrice}
                      originalPrice={product.originalPrice}
                    />
                  )}
                </StyledPrice>

                {product.tierPrices && product.tierPrices.length > 0 && (
                  <TierPriceButtons
                    productId={product.id}
                    productName={product.name}
                    tierPrices={product.tierPrices}
                  />
                )}
              </GroupPrices>

              {smOrUp && (
                <>
                  <ProductShortDescription
                    showShortDescription={true}
                    shortDescription={product.shortDescription}
                    goToAccordion={goToAccordion}
                  />

                  {!outOfStock && (
                    <InStock>{translate("global.inStock")}</InStock>
                  )}
                </>
              )}

              <Presale product={product}></Presale>

              {smOrUp && (
                <>
                  <ProductAddButton
                    canBeAddedToCart
                    canEditQuantity
                    product={product}
                    fullWidth={false}
                  />
                  <ProductReassuranceBar product={product} />
                </>
              )}
            </StyledCol>
          </Row>
        </Wrapper>
      );
    }

    /**
     * Default layout (portrait)
     */
    return (
      <Wrapper className={classnames(className, "product-item")} theme={theme}>
        <TitleWrapper theme={theme} onClick={this.handleClick}>
          <ProductTitle
            hasNoProductLink={hasNoProductLink}
            url={url}
            name={name}
            pageType={this.props.pageType}
            theme={theme}
          />
          <ProductSubtitle product={product} hideSubTitle={hideSubTitle} />
        </TitleWrapper>
        <ImageWrapper
          product={product}
          isOutOfStockClassname={isOutOfStockClassname}
          outOfStock={outOfStock}
          isWished={isWished}
          isAuthenticated={this.props.isAuthenticated}
          userWishAdd={this.props.userWishAdd}
          userWishRemove={this.props.userWishRemove}
          handleClick={this.handleClick}
          imageMaxHeight={imageMaxHeight}
          hasNoProductLink={hasNoProductLink}
          theme={theme}
          url={url}
        />
        {displayRating && <ProductRating stars={this.props.product.stars} />}
        <ProductShortDescription
          showShortDescription={showShortDescription}
          shortDescription={product.shortDescription}
        />
        <StyledPrice
          className={classnames(isOutOfStockClassname, "product-price")}
        >
          {product.displayPrice && (
            <Price
              actualPrice={product.actualPrice}
              originalPrice={product.originalPrice}
            />
          )}
        </StyledPrice>
        <Presale product={product}></Presale>
        {displayButton && (
          <ProductAddButton
            canBeAddedToCart={canBeAddedToCart}
            product={product}
          />
        )}
      </Wrapper>
    );
  }
}

export default withRenderOn(ProductWrapper);
